import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ChatComposerProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

export function ChatComposer({ onSend, disabled = false }: ChatComposerProps) {
  const { t } = useTranslation('coach');
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  function submit() {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText('');
    // Reset auto-height after clearing
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submit();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setText(e.target.value);
    // Auto-resize up to 200px
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  }

  const canSend = text.trim().length > 0 && !disabled;

  return (
    <form onSubmit={handleSubmit} className="flex items-end gap-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        rows={1}
        placeholder={t('composer.placeholder')}
        aria-label="Chat message"
        className="flex-1 resize-none rounded-xl border border-ink/20 px-4 py-3 text-sm
                   placeholder:text-ink-soft focus:outline-none focus:ring-2
                   focus:ring-leaf disabled:opacity-50
                   dark:border-ink/20 dark:bg-leaf dark:text-ink"
        style={{ minHeight: '48px', maxHeight: '200px' }}
      />
      <button
        type="submit"
        disabled={!canSend}
        aria-label="Send message"
        className="flex-shrink-0 rounded-xl px-4 py-3 text-sm font-medium min-h-[48px] min-w-[72px]
                   bg-leaf text-white hover:bg-shell
                   disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-leaf
                   dark:bg-husk dark:text-ink dark:hover:bg-leaf-deep"
      >
        {disabled ? (
          <span className="flex items-center justify-center gap-1.5">
            <span className="sr-only">{t('composer.generating')}</span>
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse"
              aria-hidden="true"
            />
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse"
              style={{ animationDelay: '150ms' }}
              aria-hidden="true"
            />
            <span
              className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse"
              style={{ animationDelay: '300ms' }}
              aria-hidden="true"
            />
          </span>
        ) : (
          t('composer.send')
        )}
      </button>
    </form>
  );
}
